import React from 'react'
import { Table, Tag, Space, Button, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import {
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import styles from '../Organizations.module.less'
import { GroupInfo } from '~/Services/GroupServices'
import { getColumnFilter } from '~/Hooks/useTableColumnFilter'

interface GroupsTableProps {
  data: GroupInfo[]
  onEdit: (group: GroupInfo) => void
  onDelete: (groupId: string) => void
  onDeleteMultiple: (groupIds: string[]) => void
  selectedRowKeys?: string[]
  onSelectionChange?: (keys: string[]) => void
  columnFilters?: Record<string, any>
  onTableChange?: (pagination: any, filters: any, sorter: any) => void
}

export const GroupsTable = ({
  data,
  onEdit,
  onDelete,
  onDeleteMultiple,
  selectedRowKeys = [],
  onSelectionChange,
  columnFilters = {},
  onTableChange,
}: GroupsTableProps) => {
  const { t } = useTranslation()

  const handleEdit = (record: GroupInfo) => {
    onEdit(record)
  }

  const handleDelete = (record: GroupInfo) => {
    if (record.id) {
      onDelete(record.id)
    }
  }

  const columns: ColumnsType<GroupInfo> = [
    {
      title: t('groups.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: true,
      render: (value?: number) => {
        const isActive = value === 1
        const text = isActive ? t('active') : t('inactive')

        return <Tag color={isActive ? 'processing' : 'default'}>{text}</Tag>
      },
    },
    {
      title: t('groups.code'),
      dataIndex: 'code',
      key: 'code',
      width: 180,
      sorter: true,
      ...getColumnFilter('code'),
    },
    {
      title: t('groups.displayName'),
      dataIndex: 'name',
      key: 'name',
      width: 280,
      sorter: true,
      ...getColumnFilter('name'),
    },
    {
      title: t('groups.note'),
      dataIndex: 'note',
      key: 'note',
      sorter: true,
      ...getColumnFilter('note'),
    },
    {
      title: (
        <Tooltip title={t('actions')}>
          <SettingOutlined />
        </Tooltip>
      ),
      key: 'actions',
      width: 120,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ]

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (onTableChange) {
      onTableChange(pagination, filters, sorter)
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      if (onSelectionChange) {
        onSelectionChange(keys as string[])
      }
    },
  }

  const tableHeight = window.innerHeight - 350

  return (
    <>
      <div className={styles.TableActions}>
        {selectedRowKeys.length > 0 && (
          <>
            <span>
              {t('selected')} <b>{selectedRowKeys.length}</b>
            </span>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                onDeleteMultiple(selectedRowKeys)
                if (onSelectionChange) {
                  onSelectionChange([])
                }
              }}
            >
              {t('remove')}
            </Button>
          </>
        )}
      </div>
      <Table
        className={styles.CustomTableScroll}
        rowKey="id"
        dataSource={data}
        columns={columns}
        pagination={false}
        bordered
        rowSelection={rowSelection}
        onChange={handleTableChange}
        scroll={{ x: 'max-content', y: tableHeight }}
      />
    </>
  )
}
